
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Plus } from "lucide-react";

interface Service {
  id: string;
  name: string;
  type: string;
  duration: string;
  price: string;
  isPublic: boolean;
  enableLiveSensor: boolean;
  categories: string[];
}

const ServiceManagement: React.FC = () => {
  const [services, setServices] = useState<Service[]>([
    {
      id: "1",
      name: "Beach Sauna",
      type: "Sauna",
      duration: "60",
      price: "25",
      isPublic: false,
      enableLiveSensor: false,
      categories: ["Beach", "Sauna"],
    },
  ]);

  const [currentService, setCurrentService] = useState(services[0]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    setCurrentService((prev) => ({ ...prev, [name]: value }));
  };

  const handleCheckboxChange = (name: string, checked: boolean) => {
    setCurrentService((prev) => ({ ...prev, [name]: checked }));
  };

  const handleTypeChange = (value: string) => {
    setCurrentService((prev) => ({ ...prev, type: value }));
  };

  const addService = () => {
    const newService = {
      id: Date.now().toString(),
      name: "",
      type: "Sauna",
      duration: "",
      price: "",
      isPublic: false,
      enableLiveSensor: false,
      categories: [],
    };
    
    setServices((prev) => [...prev, newService]);
    setCurrentService(newService);
  };

  const updateService = () => {
    setServices((prev) =>
      prev.map((service) =>
        service.id === currentService.id ? currentService : service
      )
    );
  };

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle>Services</CardTitle>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          <Input
            name="name"
            placeholder="Service Name"
            value={currentService.name}
            onChange={handleInputChange}
            className="mb-4"
          />

          <div className="grid grid-cols-1 gap-4">
            <div className="space-y-1">
              <Label htmlFor="type">Type</Label>
              <Select value={currentService.type} onValueChange={handleTypeChange}>
                <SelectTrigger>
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Sauna">Sauna</SelectItem>
                  <SelectItem value="Plunge">Plunge</SelectItem>
                  <SelectItem value="Ice Bath">Ice Bath</SelectItem>
                  <SelectItem value="Combination">Combination</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-1">
              <Label htmlFor="duration">Duration (minutes)</Label>
              <Input
                id="duration"
                name="duration"
                type="number"
                value={currentService.duration}
                onChange={handleInputChange}
              />
            </div>

            <div className="space-y-1">
              <Label htmlFor="price">Price (€)</Label>
              <Input
                id="price"
                name="price"
                type="number"
                value={currentService.price}
                onChange={handleInputChange}
              />
            </div>

            <div className="flex flex-col sm:flex-row gap-4 sm:items-center justify-between">
              <div className="flex items-center space-x-2">
                <Checkbox 
                  id="isPublic" 
                  checked={currentService.isPublic}
                  onCheckedChange={(checked) => 
                    handleCheckboxChange("isPublic", checked === true)
                  }
                />
                <Label htmlFor="isPublic">Public</Label>
              </div>
              
              <div className="flex items-center space-x-2">
                <Label htmlFor="enableLiveSensor">Enable live sensor</Label>
                <Checkbox 
                  id="enableLiveSensor" 
                  checked={currentService.enableLiveSensor}
                  onCheckedChange={(checked) => 
                    handleCheckboxChange("enableLiveSensor", checked === true)
                  }
                />
              </div>
            </div>

            <div className="space-y-1">
              <Label>Category Tags</Label>
              <div className="flex flex-wrap gap-2 mt-1">
                {currentService.categories.map((category) => (
                  <span 
                    key={category}
                    className="px-3 py-1 rounded-full bg-gray-100 text-sm"
                  >
                    {category}
                  </span>
                ))}
                {currentService.categories.length === 0 && (
                  <span className="text-sm text-gray-500">No categories added</span>
                )}
              </div>
            </div>
          </div>

          <Button 
            onClick={addService}
            className="w-full flex items-center justify-center gap-2"
          >
            <Plus size={16} /> Add Service
          </Button>
          
          <div className="text-sm text-gray-500 mt-4">
            3 of 10 Images used
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default ServiceManagement;
